/* global Excel */

/**
 * Excel adapter for the shared `finster_query` research transport. The brand
 * context and HTTP calls to the internal data agent live in
 * `shared/api/finster/queryTool.ts`; the semantic routing schema lives in
 * `finsterQueryRouting.ts`. This file adds the Excel-specific concerns:
 *
 *  - the spreadsheet-tuned tool *description* (deck builder uses its own),
 *  - brand resolution from the Excel FactSet host (`shouldUseExcelIntentBridge`)
 *    so the tool displays as `FactSet AI` / `factset_ai_query` inside FactSet
 *    and `Finster` / `finster_query` in dev,
 *  - run-scoped data sources and selected user-file IDs, and
 *  - the JSON tool-result envelope the Excel agent loop consumes.
 *
 * Use it for company / market research that is NOT derivable from the workbook
 * (earnings, filings, IR documents, consensus narrative, news, market context).
 * Standard numeric FactSet cell values still go through `generate_formula`;
 * cited company-disclosed figures are also eligible as a bounded fallback when
 * the requested breakdown is not available through a usable live formula.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  getActiveExcelContext,
  getOpenWorkbooks,
  shouldUseExcelIntentBridge,
} from "./excelHostOps";
import {
  executeFinsterQueryBuffered,
  executeFinsterQueryStream,
  FILINGS_QUERY_TOOL_NAME,
  FINSTER_QUERY_INPUT_SCHEMA,
  FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH,
  type ExcelClientContext,
  resolveFinsterBrandContext,
  type FinsterBrandContext,
  type FinsterStreamSubEvent,
} from "../shared/api/finster/queryTool";
import {
  FILINGS_LITE_DATA_SOURCES,
  getFinsterQueryDataSourcesText,
  type FinsterQueryDataSource,
  type PublicDataSourceBrandName,
} from "../shared/api/finster/constants";
import { getUserFilesResearchScopeText } from "../shared/researchSelection";
import {
  buildExcelLangfuseMetadata,
  type ExcelLangfuseRunContext,
} from "./langfuseTrace";
import {
  EXCEL_FINSTER_QUERY_INPUT_SCHEMA,
  resolveFinsterQueryRouting,
} from "./finsterQueryRouting";

/**
 * Resolve the brand context for the Excel surface. Inside the FactSet
 * spreadsheet host the research tool is branded `FactSet AI` / `factset_ai_query`;
 * everywhere else it stays `Finster` / `finster_query`. Mirrors the PowerPoint
 * `shouldUseFactSetBridge()`-driven brand selection.
 */
export function getExcelFinsterBrandContext(): FinsterBrandContext {
  return resolveFinsterBrandContext(shouldUseExcelIntentBridge() ? "FactSet AI" : "Finster");
}

export interface FinsterQueryCapabilityOptions {
  /**
   * Whether this exact model call also receives the direct company lookup
   * tool. Defaults false so chat, planner, and legacy callers never mention a
   * capability they do not expose.
   */
  includeIdLookup?: boolean;
  /**
   * Whether this prompt may tell the model to call `filings_query`.
   * Defaults true. Native ReAct passes false because that tool is absent.
   */
  includeFilingsQuery?: boolean;
  /** Expose normalized metadata and `set_citations` guidance to native ReAct only. */
  includeStructuredCitations?: boolean;
}

/**
 * Build the research tool definition surfaced to the OpenAI Responses API.
 * `strict: true` constrains decoding so the model supplies a query plus its
 * category/default or specific-capability routing decision.
 * The tool *name* follows the active brand (`finster_query` / `factset_ai_query`);
 * PowerPoint retains the smaller shared query-only schema.
 *
 * @param brand - Brand context. Defaults to the active Excel brand.
 */
export function buildFinsterQueryTool(
  brand: FinsterBrandContext = getExcelFinsterBrandContext(),
  options: FinsterQueryCapabilityOptions = {},
): ChartCapabilityToolDef {
  const name = brand.researchToolName;
  const agent = brand.publicBrandName;
  const includeFilingsQuery = options.includeFilingsQuery !== false;
  const filingsUse = includeFilingsQuery
    ? ""
    : "- SEC/global filings, investor-relations documents, presentations, and filing-based narrative.\n";
  const broaderResearch = includeFilingsQuery
    ? "- Company & competitor/peer research, business overviews, strategy, and qualitative comps narrative that require sources beyond filings and investor-relations documents.\n"
    : "- Company & competitor/peer research, business overviews, strategy, and qualitative comps narrative.\n";
  const filingsAvoid = includeFilingsQuery
    ? "- SEC filings, non-US regulatory filings, investor-relations documents, corporate presentations, risk factors, MD&A, business overviews, segment descriptions, and filing-based strategy narrative. Call `filings_query` first.\n"
    : "";
  const idLookupExclusion = options.includeIdLookup
      ? "- Company tickers/stock symbols, FactSet entity IDs (also called company IDs), or ultimate-parent IDs/names → use `id_lookup` first; use this tool only when the runtime identifies a requested field that lookup did not return. Listing and security IDs are not lookup fields, so research owns those.\n"
      : "";
  const resultContract = options.includeStructuredCitations
    ? "In native Excel mode it returns a markdown `answer` plus normalized `citations` metadata. Match citation references in the answer to those records, then copy one complete metadata record—not the ID—into `set_citations` for each supported target cell.\n\n"
    : "Returns a markdown `answer`; citations are retained separately for the Excel citations UI.\n\n";
  const groundingContract = options.includeStructuredCitations
    ? "and `citations` metadata. For each externally retrieved hardcoded fact, value, identity, or claim, select exactly one directly supporting record and copy its metadata verbatim into `set_citations`; never infer, combine, or fabricate metadata. Generic headers, labels, and units need no citation when they are presentation structure rather than retrieved information. Missing metadata does not block an otherwise valid workbook value."
    : "and `citations`. When provided, copy exact citation IDs onto sourced numbers and substantive non-numeric claims only. Missing IDs never block an otherwise valid workbook value. Company names, filing types/dates, periods, metric labels, units, headings, formulas, and other descriptive metadata remain uncited. The runtime attempts yellow source Notes silently on a best-effort basis.";
  return {
    name,
    description:
      `Delegates a targeted, stateless knowledge-work request to the internal ${agent} data agent. ` +
      "The called agent cannot inspect the workbook, cells, formulas, selected range, " +
      "conversation, previous calls, or your reasoning. Read required workbook context first " +
      "and copy every necessary premise and value into `query`. " +
      resultContract +
      "HARD ENTITY LIMIT: one call may cover at most 10 distinct companies or other " +
      "entities. Split larger populations into complete, non-overlapping calls while " +
      "preserving stable row/entity identifiers. Reuse completed results and continue " +
      "until every in-scope entity is covered or explicitly unresolved.\n\n" +
      "Use `tool_categories` with `tool_names: []` for normal routing. Populate " +
      "`tool_names` only when a specific named capability is required; a non-empty " +
      "list overrides the categories. Select the smallest sufficient scope.\n\n" +
      "USE FOR:\n" +
      filingsUse +
      broaderResearch +
      "- Latest-earnings summaries, management commentary, guidance, and executive quotes.\n" +
      "- Earnings transcripts, news, and market context.\n" +
      "- Consensus/estimates narrative and other analyst-style commentary.\n" +
      "- Explicit company-disclosed numeric tables or breakdowns when a bounded `generate_formula` attempt cannot produce a usable result. Use only clearly labeled figures and periods present in the cited answer.\n" +
      "- Any qualitative document research that might otherwise tempt `generate_formula` / TTF / FDS.\n\n" +
      "DO NOT USE FOR:\n" +
      filingsAvoid +
      "- Standard numeric FactSet cell values / formulas when `generate_formula` can provide them.\n" +
      idLookupExclusion +
      "- Questions answerable directly from the current workbook context.\n\n" +
      "STATELESS BOUNDARY: this called research agent cannot see the workbook, " +
      "chat history, or prior tool results. Make the query self-contained with " +
      "the company, periods, metrics, units, workbook facts, and requested " +
      "response format needed to answer correctly. A reader who sees only the query " +
      "must have every premise needed to complete it; otherwise, read the missing " +
      "workbook context first and include the actual facts rather than references " +
      "such as `this workbook`, `the selected range`, or `as above`. Decompose an " +
      "overly broad request into bounded, coherent subqueries by company, topic, " +
      "period, source, or analytical question. Run independent subqueries in parallel; " +
      "sequence genuinely dependent subqueries only when one answer is needed to frame " +
      "the next, and repeat every required premise in each call.\n\n" +
      "GROUNDING: Never fabricate company facts, financials, or narrative — " +
      `pull them via this tool and base your answer on the returned \`answer\` ` +
      groundingContract,
    strict: true,
    inputSchema:
      EXCEL_FINSTER_QUERY_INPUT_SCHEMA as unknown as ChartCapabilityToolDef["inputSchema"],
  };
}

/**
 * Default research tool definition (Finster brand). Brand-aware call sites
 * should prefer {@link buildFinsterQueryTool}; this static export backs the
 * registry's tool list and unknown-tool fallback.
 */
export const FINSTER_QUERY_TOOL: ChartCapabilityToolDef = buildFinsterQueryTool(
  resolveFinsterBrandContext("Finster"),
);

/**
 * Fast, brand-independent research route pinned to official filings and IR
 * documents. It uses the same transport and result contract as the full
 * research tool but avoids unnecessary multi-source fan-out.
 */
export const FILINGS_QUERY_TOOL: ChartCapabilityToolDef = {
  name: FILINGS_QUERY_TOOL_NAME,
  description:
    "Fast filings/IR-only company research. Pinned to SEC filings, " +
    "investor-relations documents and corporate presentations, and non-US " +
    "Global Filings. Prefer this route for filing narrative because it avoids " +
    "the full research tool's broad source fan-out.\n\n" +
    "USE FOR:\n" +
    "- Latest 10-K, 10-Q, 8-K, proxy, and other SEC filing summaries, risk factors, and MD&A.\n" +
    "- Non-US annual reports and regulatory filings.\n" +
    "- Investor-relations decks, prospectuses, corporate presentations, business overviews, segments, and strategy narrative.\n" +
    "- Explicit company-disclosed figures that are clearly labeled in the returned answer.\n\n" +
    "DO NOT USE FOR:\n" +
    "- Earnings-call transcripts, management commentary, guidance, estimates, consensus, news, market data, expert-network, broker, industry, uploaded-file, or ADIA research; use the full research tool.\n" +
    "- Standard numeric FactSet cell values that generate_formula can produce.\n\n" +
    "The called research agent is stateless and cannot see the workbook or this conversation. " +
    "Make every query self-contained with the company, filing, periods, metrics, units, " +
    "workbook facts, and exact deliverable it needs. A reader who sees only the query " +
    "must have every premise needed to complete it; otherwise, read the missing workbook " +
    "context first and include the actual facts rather than references such as `this workbook`, " +
    "`the selected range`, or `as above`. Decompose an overly broad request into bounded, " +
    "coherent subqueries by company, filing, period, topic, or analytical question. Run " +
    "independent subqueries in parallel; sequence genuinely dependent subqueries only when " +
    "one answer is needed to frame the next, and repeat every required premise in each call. " +
    "Never fabricate facts or citations.",
  strict: true,
  inputSchema: FINSTER_QUERY_INPUT_SCHEMA as unknown as ChartCapabilityToolDef["inputSchema"],
};

export interface FinsterQueryToolContext {
  dataSources?: readonly FinsterQueryDataSource[];
  manualUserFileIds?: readonly string[];
  skillTargetFileIds?: readonly string[];
  userFileIds?: readonly string[];
  selectionMode?: "default" | "filtered" | "user_files";
  scopeAvailable?: boolean;
  onSubEvent?: (event: FinsterStreamSubEvent) => void;
  /**
   * Run-pinned workbook / sheet identity. Used when live host context is
   * unavailable so Finster Chat can still group Excel sessions by file.
   */
  workbookName?: string;
  sheetName?: string;
  sheetNumber?: number;
  /**
   * Chat-tab id (ULID) shared by every research turn in this tab. Forwarded as
   * `session_id` so follow-up prompts append to one Finster Chat session.
   */
  chatSessionId?: string;
  langfuseContext?: ExcelLangfuseRunContext;
  /** Buffered for a parallel research group; streaming for a standalone call. */
  executionMode?: "stream" | "buffered";
  /**
   * Exact resolved user-Skill guidance for this run. Forwarded verbatim as
   * `selected_skills_guidance`; never concatenated into `query`.
   */
  userSkillGuidance?: string;
}

function normalizeExcelContextValue(
  value: string | undefined,
  maxLength: number
): string | undefined {
  const normalized = value?.normalize("NFKC").trim().replace(/\s+/g, " ");
  if (!normalized) return undefined;
  if (Array.from(normalized).length > maxLength) return undefined;
  return normalized;
}

function deriveExcelWorkbookName(name: string | undefined): string | undefined {
  return normalizeExcelContextValue(
    name,
    FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH
  );
}

function mergeExcelClientContext(
  ...parts: Array<ExcelClientContext | undefined>
): ExcelClientContext | undefined {
  const merged: ExcelClientContext = {};
  for (const part of parts) {
    if (!part) continue;
    if (part.workbook_id && !merged.workbook_id) merged.workbook_id = part.workbook_id;
    if (part.workbook_name && !merged.workbook_name) {
      merged.workbook_name = part.workbook_name;
    }
    if (part.sheet_id && !merged.sheet_id) merged.sheet_id = part.sheet_id;
    if (part.sheet_name && !merged.sheet_name) merged.sheet_name = part.sheet_name;
    if (part.sheet_number !== undefined && merged.sheet_number === undefined) {
      merged.sheet_number = part.sheet_number;
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function fallbackExcelClientContext(
  context?: Pick<
    FinsterQueryToolContext,
    "workbookName" | "sheetName" | "sheetNumber"
  >
): ExcelClientContext | undefined {
  if (!context) return undefined;
  const workbookName = deriveExcelWorkbookName(context.workbookName);
  const sheetName = normalizeExcelContextValue(
    context.sheetName,
    FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH
  );
  const sheetNumber =
    typeof context.sheetNumber === "number" &&
    Number.isFinite(context.sheetNumber) &&
    context.sheetNumber > 0
      ? Math.trunc(context.sheetNumber)
      : undefined;
  return mergeExcelClientContext({
    ...(workbookName ? { workbook_name: workbookName } : {}),
    ...(sheetName ? { sheet_name: sheetName } : {}),
    ...(sheetNumber !== undefined ? { sheet_number: sheetNumber } : {}),
  });
}

/**
 * FactSet's GetActiveExcelContext often omits `workbookName`. Open-workbook
 * listing still reports the active book — use that so Finster Chat can group
 * Excel sessions the same way PowerPoint groups by presentation name.
 */
async function resolveActiveWorkbookNameFromOpenWorkbooks(): Promise<
  string | undefined
> {
  if (!shouldUseExcelIntentBridge()) return undefined;
  try {
    const openWorkbooks = await getOpenWorkbooks();
    const active =
      openWorkbooks.find((workbook) => workbook.isActive === true) ??
      openWorkbooks[0];
    return deriveExcelWorkbookName(active?.name ?? active?.fullName);
  } catch (error) {
    console.warn(
      "[Excel] GetOpenWorkbooks failed while resolving workbook name:",
      error
    );
    return undefined;
  }
}

/**
 * Host RPCs for attribution must never stall the research HTTP call. Mid-run
 * FactSet bridge calls often queue behind workbook reads/writes; without a
 * budget, `finster_query` hangs before it even hits `/queries-v4/stream`.
 */
const EXCEL_CLIENT_CONTEXT_HOST_TIMEOUT_MS = 1_500;

async function withTimeoutOrUndefined<T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => {
          console.warn(
            `[Excel] ${label} timed out after ${timeoutMs}ms; continuing without it`
          );
          resolve(undefined);
        }, timeoutMs);
      }),
    ]);
  } catch (error) {
    console.warn(`[Excel] ${label} failed:`, error);
    return undefined;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function buildExcelClientContextFromBridge(): Promise<
  ExcelClientContext | undefined
> {
  const ctx = await getActiveExcelContext();
  const workbookName = deriveExcelWorkbookName(ctx.workbookName);
  const sheetName = normalizeExcelContextValue(
    ctx.activeSheet?.name,
    FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH
  );
  const sheetNumber =
    typeof ctx.activeSheet?.index === "number" &&
    Number.isFinite(ctx.activeSheet.index) &&
    ctx.activeSheet.index > 0
      ? Math.trunc(ctx.activeSheet.index)
      : undefined;
  return mergeExcelClientContext({
    ...(workbookName ? { workbook_name: workbookName } : {}),
    ...(sheetName ? { sheet_name: sheetName } : {}),
    ...(sheetNumber !== undefined ? { sheet_number: sheetNumber } : {}),
  });
}

async function buildExcelClientContextFromOfficeJs(): Promise<
  ExcelClientContext | undefined
> {
  const excelApi = (globalThis as typeof globalThis & {
    Excel?: {
      run<T>(callback: (context: any) => Promise<T>): Promise<T>;
    };
  }).Excel;
  if (!excelApi) return undefined;

  return excelApi.run(async (context: any) => {
    const workbook = context.workbook;
    workbook.load("name");
    const sheet = workbook.worksheets.getActiveWorksheet();
    sheet.load(["name", "position"]);
    await context.sync();

    const workbookName = deriveExcelWorkbookName(workbook.name);
    const sheetName = normalizeExcelContextValue(
      sheet.name,
      FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH
    );
    // Office.js `position` is 0-based; expose 1-based sheet numbers to match
    // the PowerPoint slide_number convention and FactSet host indexing.
    const sheetNumber =
      typeof sheet.position === "number" && Number.isFinite(sheet.position)
        ? Math.trunc(sheet.position) + 1
        : undefined;

    return mergeExcelClientContext({
      ...(workbookName ? { workbook_name: workbookName } : {}),
      ...(sheetName ? { sheet_name: sheetName } : {}),
      ...(sheetNumber !== undefined && sheetNumber > 0
        ? { sheet_number: sheetNumber }
        : {}),
    });
  });
}

async function probeHostExcelClientContext(): Promise<
  ExcelClientContext | undefined
> {
  if (shouldUseExcelIntentBridge()) {
    try {
      return await buildExcelClientContextFromBridge();
    } catch (bridgeError) {
      console.warn(
        "[Excel] Bridge client context failed; trying Office.js fallback:",
        bridgeError
      );
      return buildExcelClientContextFromOfficeJs();
    }
  }
  return buildExcelClientContextFromOfficeJs();
}

/**
 * Capture the active workbook/sheet identity for session attribution in
 * Finster Chat. Best-effort — never blocks the research query if Office/host
 * context is unavailable. Prefer the run pin (same role as PPT's in-memory
 * presentationRef), then a short-budget live host probe.
 */
export async function buildExcelClientContext(
  fallbacks?: Pick<
    FinsterQueryToolContext,
    "workbookName" | "sheetName" | "sheetNumber"
  >
): Promise<ExcelClientContext | undefined> {
  const fallbackContext = fallbackExcelClientContext(fallbacks);

  // The executor already pinned the workbook for this run. Re-probing the host
  // here can hang behind in-flight Excel intents and stall research entirely.
  if (fallbackContext?.workbook_name) {
    return fallbackContext;
  }

  const hostContext = await withTimeoutOrUndefined(
    probeHostExcelClientContext(),
    EXCEL_CLIENT_CONTEXT_HOST_TIMEOUT_MS,
    "client context host probe"
  );

  let merged = mergeExcelClientContext(hostContext, fallbackContext);
  if (!merged?.workbook_name) {
    const openWorkbookName = await withTimeoutOrUndefined(
      resolveActiveWorkbookNameFromOpenWorkbooks(),
      EXCEL_CLIENT_CONTEXT_HOST_TIMEOUT_MS,
      "GetOpenWorkbooks workbook-name probe"
    );
    merged = mergeExcelClientContext(
      merged,
      openWorkbookName ? { workbook_name: openWorkbookName } : undefined
    );
  }
  return merged;
}

/**
 * Instruction appended to the tool-enabled Excel system prompts to steer use
 * of the research tool. Without explicit steering the model underuses it and
 * tends to invent narrative/financial content. Brand-aware so the FactSet host
 * sees `factset_ai_query` / `FactSet AI` and dev sees `finster_query` / `Finster`.
 * Returned fresh (not a module-eval constant) so the brand is resolved at
 * prompt-build time.
 */
export function getFinsterQueryToolInstruction(
  brand: FinsterBrandContext = getExcelFinsterBrandContext(),
  context?: FinsterQueryToolContext,
  options: FinsterQueryCapabilityOptions = {},
): string {
  const tool = brand.researchToolName;
  const agent = brand.publicBrandName;
  const userFileCount = new Set(
    (context?.userFileIds ?? []).map((fileId) => fileId.trim()).filter(Boolean)
  ).size;
  const manualUserFileCount = new Set(
    (context?.manualUserFileIds ?? context?.userFileIds ?? [])
      .map((fileId) => fileId.trim())
      .filter(Boolean)
  ).size;
  const skillFileCount = new Set(
    (context?.skillTargetFileIds ?? [])
      .map((fileId) => fileId.trim())
      .filter(Boolean)
  ).size;
  const scopedDataSources = resolveDataSources(context);
  const dataSourcesText =
    !context || context.scopeAvailable === false
      ? "Research source scope unavailable"
      : buildResearchScopeText(
          scopedDataSources,
          userFileCount,
          manualUserFileCount,
          skillFileCount,
          agent
        );
  const idLookupRouting = options.includeIdLookup
      ? `- **Company identity routing**: for one company's ticker/stock symbol, FactSet entity ID (also called a company ID), or ultimate-parent ID/name, use \`id_lookup\` first even when the user does not name the tool. Listing and security IDs do not come back from lookup, so research owns those. Do not call \`${tool}\` in parallel with lookup or when one candidate contains every requested field. Use \`${tool}\` only when the runtime marks a requested field as still missing; a coarse parent-enrichment fallback flag does not justify research for an already-returned ticker or entity ID. If multiple candidates remain plausible, ask the user rather than auto-selecting one.
`
      : "";
  const idLookupExamples = options.includeIdLookup
      ? `- "What is Apple's ticker?" → \`id_lookup\`; use \`${tool}\` only if the ticker remains unavailable and the runtime requests fallback.
- "What are Apple’s FactSet entity ID and ultimate parent?" → \`id_lookup\`; use \`${tool}\` only for a requested field the runtime marks as still unavailable.
`
      : "";
  const repeatedResearchScope = "";
  const includeFilingsQuery = options.includeFilingsQuery !== false;
  const researchAccess = includeFilingsQuery
    ? `You have access to \`filings_query\` for focused filings/IR research and \`${tool}\`, the internal ${agent} data agent, for broader company / market research that is NOT derivable from the workbook. Both return a markdown answer; citations are retained separately for the Excel citations UI.`
    : `You have access to \`${tool}\`, the internal ${agent} data agent, for company and market research that is NOT derivable from the workbook, including filings and investor-relations documents. It returns a markdown answer; citations are retained separately for the Excel citations UI.`;
  const filingsRouting = includeFilingsQuery
    ? `- **Filings-first routing**: use \`filings_query\` first for SEC filings, non-US regulatory filings, investor-relations documents, corporate presentations, risk factors, MD&A, business overviews, segment descriptions, and filing-based strategy narrative. It is pinned to official filings and IR sources and avoids broad source fan-out. Use \`${tool}\` for earnings calls, management commentary or guidance transcripts, estimates, consensus, news, market data, expert-network, broker, industry, uploaded-file, or other broader research.`
    : `- **Research routing**: use \`${tool}\` for SEC filings, non-US regulatory filings, investor-relations documents, corporate presentations, risk factors, MD&A, business overviews, segment descriptions, filing-based strategy narrative, earnings calls, management commentary, guidance transcripts, estimates, consensus, news, market data, and uploaded-file research.`;
  const sourceConstrainedNumeric = includeFilingsQuery
    ? "when the user asks for values from SEC filings, annual reports, or official company documents, call `filings_query`; when selected uploaded files are required, call `" +
      tool +
      "`."
    : "when the user asks for values from SEC filings, annual reports, official company documents, or selected uploaded files, call `" +
      tool +
      "`.";
  const mandatorySecAction = includeFilingsQuery
    ? `call \`filings_query\` as the first substantive action. Escalate to \`${tool}\` only when the required evidence falls outside filings/IR scope.`
    : `call \`${tool}\` as the first substantive action.`;
  const citedNumericFallback = includeFilingsQuery
    ? `use \`filings_query\` for figures disclosed in filings/IR or \`${tool}\` for figures requiring broader or uploaded-file sources.`
    : `use \`${tool}\` for figures disclosed in filings, investor-relations documents, or broader sources.`;
  const filingExamples = includeFilingsQuery
    ? `- "Summarize a company's latest SEC filing" → \`filings_query\`.
- "What are Apple's latest 10-K risk factors?" → \`filings_query\`.`
    : `- "Summarize a company's latest SEC filing" → \`${tool}\`.
- "What are Apple's latest 10-K risk factors?" → \`${tool}\`.`;
  const citationGuidance = options.includeStructuredCitations
    ? "- **Citations on workbook writes**: for each externally retrieved hardcoded fact, value, identity, or claim, choose exactly one directly supporting normalized citation record and pass its complete metadata—not its citation ID—to `set_citations` for that target cell. Generic headers, labels, units, formulas, and blank cells remain uncited. Never infer, combine, or fabricate metadata. Missing metadata does not block an otherwise valid workbook value."
    : "- **Citations on workbook writes**: attach matching exact IDs to hardcoded sourced numbers when the research response provides them. Cite a non-numeric cell only when it states a substantive, externally verifiable claim such as a business assertion, quote, guidance statement, explanation, risk, or conclusion. Descriptive metadata is not a claim: company/entity names, tickers, filing/report types, filing/publication dates, fiscal periods, metric labels, units, headings, and source labels remain uncited. In a descriptor-plus-number table row, cite the number cell only. Never invent IDs and never hand-author the source Note text. If an ID is absent, still write the otherwise valid sourced value. Source Note attachment is silent best effort and must never be retried, reported, or treated as a workbook-write failure.";
  return `

## Company & Market Research (\`${tool}\`)

CRITICAL FACTUAL POLICY: ${researchAccess}

\`${tool}\` data sources for this request: ${dataSourcesText}.

${filingsRouting}
- **Stateless research boundary**: neither called research agent can see this workbook, the chat, or prior tool results. Every research query must be self-contained: include the company/entity, exact question and deliverable, relevant periods and units, and every workbook fact or premise the called agent needs. Never say "this workbook", "the selected range", or "as above" without supplying the actual context.
- **Broad-scope decomposition**: decompose an overly broad request into bounded, coherent subqueries by company, topic, period, source, or analytical question. Run independent subqueries in parallel. Sequence genuinely dependent subqueries only when one answer is needed to frame the next, and make every later query self-contained by repeating the required premise and relevant earlier evidence.
- **Hard 10-entity maximum**: never include more than 10 distinct companies or other entities in one \`${tool}\` call. For larger populations, use complete non-overlapping calls, preserve stable row/entity identifiers, reuse successful results, and continue until every in-scope entity is covered or explicitly unresolved.
- **Hard source scope**: the sources listed above are an allowlist, not a preference. Never substitute an unselected source, model knowledge, an adjacent workbook value, or an existing workbook formula.
${context?.selectionMode === "filtered" ? `- **Filtered-source routing**: decide from the meaning of the request whether it needs external facts/content or only workbook operations. Use the appropriate research route for external facts/content and stay within the exact allowlist above. Clearly workbook-only work may proceed without research.` : ""}
${manualUserFileCount > 0 ? `- **Manually selected files are mandatory**: the user explicitly selected ${manualUserFileCount} uploaded ${manualUserFileCount === 1 ? "file" : "files"}. Call \`${tool}\` before the final answer and before using content from those uploaded files in a workbook write. This does not give the research agent access to the open Excel workbook. Other Excel tools remain available for independent workbook work. If uploaded-file retrieval fails, report that failure; do not substitute an unlisted external source.` : ""}
${skillFileCount > 0 ? `- **Skill files are not manual selections**: ${skillFileCount} ${skillFileCount === 1 ? "file is" : "files are"} attached by active Skill policy for research. Use them only when the Skill guidance and current task make them relevant. Never describe them as files the user explicitly selected, and never confuse them with the open Excel workbook.` : ""}
- **Source-constrained numeric requests**: ${sourceConstrainedNumeric} Use the appropriate route even though the requested output is numeric. Write the retrieved values as static values when requested.
- **User restrictions override formula routing**: if the user says not to use FDS/FactSet formulas, not to map/copy a nearby column, or to use a named source only, do not call \`generate_formula\` and do not reuse workbook values for that data.
- **Use \`${tool}\`** for broader competitor / peer analysis, latest-earnings summaries, management commentary and guidance, news, market context, consensus / estimates narrative, uploaded files, or any request that needs sources beyond filings/IR.
${idLookupRouting}- **Mandatory SEC routing**: if the user asks to summarize, explain, or analyze a company's latest SEC filing / 10-K / 10-Q / 8-K, ${mandatorySecAction} Do not answer from model knowledge, do not say you lack access to a tool, and do not ask for clarification unless the company cannot be identified.
- **Use \`generate_formula\` first** for standard numeric FactSet cell values that belong in the workbook, but only when the user has not constrained the source or prohibited formulas.
- **Cited numeric fallback**: after a bounded formula attempt cannot produce the requested dataset, ${citedNumericFallback} Ask for the exact labels, periods, units, and reporting basis needed. Write only figures that are clearly present in the returned answer with citations; never infer missing values or silently mix reporting bases.
- **Research before write**: before writing qualitative workbook content (competitor analysis sections, earnings summaries, SEC-based bullets, business overviews, or strategy notes), use the appropriate research route before writing and ground the cells or answer in its returned \`answer\` and \`citations\`.
${citationGuidance}
- **Do NOT use \`generate_formula\`, TTF, FDS, or model knowledge** for SEC filings, earnings transcripts, IR documents, management commentary, business strategy, industry research, qualitative comps narrative, or "what did management say" questions.
- **Do not skip retrieval**: do not say you could not retrieve, access, or summarize external company documents unless you first called the appropriate research route and that tool returned an error or no relevant evidence.
- **Never fabricate** company facts, financials, or narrative. If unsure whether the workbook already contains the needed fact, use workbook reads first; if it is not present, call the appropriate research route.

### Large repeated company/entity research
${repeatedResearchScope}
- Preserve each worksheet row's stable identifier in the query. Ask for one brief, clearly labeled answer per identifier so you can map the free-form markdown response back to the correct row.
- Keep every request self-contained and concise. Wait for the parallel group to return before interpreting the answers or writing worksheet results.
- Treat this as best-effort research: use every successful response and continue making progress. Treat incomplete, insufficient, or low-quality results as remaining gaps rather than fabricating answers or failing the entire workbook write.
- Use judgement to determine whether a materially different follow-up is likely to resolve a remaining gap. Do not repeat the same failed request. When reasonable alternatives have been exhausted, make sure you record the unresolved item and its limitation.
- Do not request JSON, a JSON schema, or another structured-output contract; use the ordinary markdown answer from the chosen research route.

Routing examples:
- "Summarize NVIDIA's latest earnings call" → \`${tool}\`.
${filingExamples}
- "Build a Datadog competitor analysis with business overview and earnings highlights" → \`${tool}\` before writing cells.
- "Add AAPL revenue for the last 8 quarters in cells" → \`generate_formula\`.
${idLookupExamples}- "Explain this selected workbook table" → read workbook context; no external tool unless facts are missing.`;
}

/**
 * Describe the active research scope. Uploaded files and ordinary sources are
 * independent selections, so a run carrying both must present them as one
 * combined allowlist; only a run with no ordinary sources is files-only.
 */
function buildResearchScopeText(
  dataSources: readonly FinsterQueryDataSource[],
  userFileCount: number,
  manualUserFileCount: number,
  skillFileCount: number,
  agent: PublicDataSourceBrandName,
): string {
  if (userFileCount === 0) {
    return getFinsterQueryDataSourcesText(dataSources, agent);
  }
  const fileParts: string[] = [];
  if (manualUserFileCount > 0) {
    fileParts.push(getUserFilesResearchScopeText(manualUserFileCount));
  }
  if (skillFileCount > 0) {
    fileParts.push(
      `${skillFileCount} Skill-attached ${skillFileCount === 1 ? "file" : "files"} available through research (not manually selected)`
    );
  }
  const unattributedFileCount = Math.max(
    0,
    userFileCount - manualUserFileCount - skillFileCount
  );
  if (unattributedFileCount > 0) {
    fileParts.push(
      `${unattributedFileCount} additional policy-scoped ${unattributedFileCount === 1 ? "file" : "files"}`
    );
  }
  const userFilesText = fileParts.join("; ");
  return dataSources.length === 0
    ? `${userFilesText}; no ordinary data sources are selected — these uploaded files are the ONLY retrieval scope`
    : `${getFinsterQueryDataSourcesText(dataSources, agent)}; ${userFilesText}`;
}

/**
 * Resolve the run-scoped data sources. Missing context resolves to no sources;
 * execution fails closed below instead of silently broadening to public data.
 *
 * @returns The data sources to send to the data agent.
 */
function resolveDataSources(
  context?: FinsterQueryToolContext,
): readonly FinsterQueryDataSource[] {
  return context?.dataSources ?? [];
}

/**
 * Apply one model routing decision to the immutable research scope captured for
 * this Excel run.
 *
 * Explicit filtered selections are hard allowlists and are intersected with
 * model routing. A files-only run stays files-only; exact external capabilities
 * fail rather than silently escaping that scope. Default selection is not a
 * user restriction, so semantic routes may use hidden-but-supported source
 * codes while data-feed remains authoritative for entitlement checks. Legacy
 * query-only callers keep the original source snapshot unchanged.
 *
 * @param input - Parsed, untrusted `finster_query` arguments.
 * @param context - Run-pinned source, file, and selection-mode snapshot.
 * @returns Resolved data-feed sources, or a model-recoverable routing error.
 */
function resolveRoutedDataSources(
  input: Record<string, unknown>,
  context: FinsterQueryToolContext,
): { dataSources?: readonly FinsterQueryDataSource[]; error?: string } {
  const routing = resolveFinsterQueryRouting(input);
  if (routing.mode === "invalid") {
    return { error: routing.error };
  }
  if (routing.mode === "legacy") {
    return { dataSources: resolveDataSources(context) };
  }

  const selectedSources = context.dataSources ?? [];
  const userFileCount = context.userFileIds?.length ?? 0;
  if (selectedSources.length === 0 && userFileCount > 0) {
    if (routing.mode === "specific_capabilities") {
      return {
        error:
          "The selected research scope contains uploaded files only, so the requested " +
          "specific external capabilities are unavailable.",
      };
    }
    return { dataSources: [] };
  }

  if (context.selectionMode !== "filtered") {
    return { dataSources: routing.dataSources };
  }

  const selected = new Set(selectedSources);
  const intersection = routing.dataSources.filter((source) => selected.has(source));
  if (intersection.length === 0) {
    return {
      error:
        "The requested research categories or capabilities are outside the selected " +
        "data-source scope. Choose routing that is compatible with the available sources.",
    };
  }
  return { dataSources: intersection };
}

/**
 * Detect a research-agent refusal that arrived through the transport's normal
 * answer channel. The upstream request technically succeeded, but the answer
 * contains no usable evidence and must be recoverable by the owning ReAct
 * loop rather than displayed as a successful research result.
 *
 * Citations win over refusal wording because a useful partial answer may
 * honestly state that one requested fact could not be verified.
 */
export function isEvidenceFreeResearchRefusal(
  answer: string,
  citations?: readonly unknown[],
): boolean {
  if ((citations?.length ?? 0) > 0) return false;
  const normalized = answer
    .normalize("NFKC")
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!normalized) return false;

  const accessDenial =
    /\b(?:i|we)\s+(?:cannot|can not|can't|am unable|are unable|do not have access|don't have access)\b.{0,160}\b(?:access|open|query|retrieve|read)\b/.test(
      normalized,
    );
  const completionRefusal =
    /\b(?:i|we)\s+(?:cannot|can not|can't|am unable|are unable)\b.{0,160}\b(?:complete|extract|cite|enumerate)\b/.test(
      normalized,
    );
  const unavailableEvidence =
    /\b(?:not available to (?:me|us)|not available in (?:the )?current sources|no access to)\b/.test(
      normalized,
    );
  const asksForResubmission =
    /\b(?:please|you (?:will )?need to)\s+(?:provide|upload|paste|export|share)\b/.test(
      normalized,
    );

  return (
    accessDenial ||
    (completionRefusal && (unavailableEvidence || asksForResubmission)) ||
    (unavailableEvidence && asksForResubmission)
  );
}

/**
 * Select the citation envelopes used by native Excel.
 *
 * Finster Chat treats a non-empty final-response citation projection as
 * authoritative. Only when that projection is absent does it retain the
 * deduplicated union of delegated-agent citations. The native runner later
 * resolves the final answer's citation references against this selected set
 * while projecting compact metadata for the model.
 */
export function reconcileResearchCitations(
  rootCitations: readonly unknown[],
  delegatedCitations: readonly unknown[],
): unknown[] {
  if (rootCitations.length > 0) return [...rootCitations];

  const seen = new Set<string>();
  return delegatedCitations.filter((citation, index) => {
    let key: string;
    try {
      key = JSON.stringify(citation) ?? `undefined:${index}`;
    } catch {
      key = `unserializable:${index}`;
    }
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Execute a research tool call (`finster_query` / `factset_ai_query`) and return
 * both the model-facing JSON envelope and optional Langfuse observation metadata.
 * Delegates the HTTP call to the shared streaming query core and maps the
 * result into the JSON envelope the model consumes. On a non-OK response or
 * thrown (non-abort) error, the envelope is `{ error }` so the agent can recover
 * gracefully. Caller cancellation (`AbortError`) propagates so the surrounding
 * tool loop handles it.
 *
 * The upstream FactSet session id rides ONLY on `observationMetadata` (it is
 * kept out of the model-facing `output`), mirroring the PowerPoint tool path so
 * the trace correlates with the Finster run without changing agent behavior.
 *
 * @param rawInput - Parsed JSON arguments from the LLM. New model calls include
 *   `{ query, tool_categories, tool_names }`; legacy query-only callers retain
 *   the run-scoped source snapshot.
 * @param signal - Optional `AbortSignal` to cancel the underlying HTTP request.
 * @param context - Run-scoped data sources, selected file ids, and stream callback.
 * @returns `{ output, observationMetadata? }` — `output` is the serialized JSON
 *   string (`{ answer }` on success, else `{ error }`).
 */
export async function executeFinsterQueryToolDetailed(
  rawInput: unknown,
  signal?: AbortSignal,
  context?: FinsterQueryToolContext,
): Promise<{
  output: string;
  observationMetadata?: Record<string, unknown>;
  researchCitations?: unknown[];
}> {
  const input = rawInput as Record<string, unknown> | null;
  const query = typeof input?.query === "string" ? input.query : "";
  if (!query.trim()) {
    return { output: JSON.stringify({ error: "Missing required parameter: query" }) };
  }
  if (
    !context ||
    context.scopeAvailable === false ||
    !Array.isArray(context.dataSources) ||
    !Array.isArray(context.userFileIds)
  ) {
    return {
      output: JSON.stringify({
        error:
          "Research source scope is unavailable for this Excel run. " +
          "The request was not sent to avoid using unselected sources.",
      }),
    };
  }
  if (
    context.selectionMode === "filtered" &&
    context.dataSources.length === 0 &&
    context.userFileIds.length === 0
  ) {
    return {
      output: JSON.stringify({
        error:
          "No research data sources are selected for this Excel run. " +
          "The request was not sent to avoid automatic source selection.",
      }),
    };
  }
  if (
    context.selectionMode === "user_files" &&
    context.userFileIds.length === 0
  ) {
    return {
      output: JSON.stringify({
        error:
          "The selected uploaded files are unavailable for this Excel run. " +
          "The request was not sent to another source.",
      }),
    };
  }
  const routedSources = resolveRoutedDataSources(input ?? {}, context);
  if (routedSources.error || !routedSources.dataSources) {
    return {
      output: JSON.stringify({
        error: routedSources.error ?? "Unable to resolve research routing.",
      }),
    };
  }
  const executeQuery =
    context?.executionMode === "buffered"
      ? executeFinsterQueryBuffered
      : executeFinsterQueryStream;
  const result = await executeQuery({
    query,
    deliveryChannel: "excel",
    researchTransport: "meta_agent",
    chatSessionId: context?.chatSessionId,
    clientOrigin: "excel_addin",
    clientContext: await buildExcelClientContext({
      workbookName: context?.workbookName,
      sheetName: context?.sheetName,
      sheetNumber: context?.sheetNumber,
    }),
    dataSources: routedSources.dataSources,
    userFileIds: context?.userFileIds,
    ...(context?.userSkillGuidance !== undefined
      ? { selectedSkillsGuidance: context.userSkillGuidance }
      : {}),
    publicBrandName: getExcelFinsterBrandContext().publicBrandName,
    signal,
    ...(context?.executionMode === "buffered"
      ? {}
      : { onSubEvent: context?.onSubEvent }),
    langfuseMetadata: context?.langfuseContext
      ? buildExcelLangfuseMetadata(context.langfuseContext, {
          phase: "finster_query",
        })
      : undefined,
  });

  if (!result.success) {
    return { output: JSON.stringify({ error: result.error ?? "finster_query failed" }) };
  }

  const rootCitations = Array.isArray(result.rootCitations)
    ? result.rootCitations
    : Array.isArray(result.citations)
      ? result.citations
      : [];
  const delegatedCitations = Array.isArray(result.delegatedCitations)
    ? result.delegatedCitations
    : [];
  const selectedCitations = reconcileResearchCitations(
    rootCitations,
    delegatedCitations,
  );
  const researchCitations =
    selectedCitations.length > 0 ? selectedCitations : undefined;
  if (isEvidenceFreeResearchRefusal(result.answer ?? "", researchCitations)) {
    return {
      output: JSON.stringify({
        ok: false,
        usableEvidence: false,
        retryable: true,
        reason: "research_returned_refusal",
        error:
          "The research agent could not access the requested evidence. " +
          "Use workbook tools for the open Excel workbook, or retry with a materially different self-contained query against sources the research agent can access.",
        answer: result.answer,
      }),
    };
  }

  const observationMetadata: Record<string, unknown> = {
    ...(result.sessionId ? { factsetSessionId: result.sessionId } : {}),
    ...(result.sessionName ? { factsetSessionName: result.sessionName } : {}),
  };

  return {
    output: JSON.stringify({ answer: result.answer }),
    ...(Object.keys(observationMetadata).length > 0 ? { observationMetadata } : {}),
    ...(researchCitations ? { researchCitations } : {}),
  };
}

/**
 * Resolve the official-document allowlist for one focused filings call.
 *
 * Default mode uses the complete pinned bundle. An explicit user filter is
 * authoritative, so only its intersection with that bundle is allowed.
 *
 * @returns A non-empty source list, or `null` when an explicit filter contains
 * no filings/IR source and the caller must recover through the full tool.
 */
export function resolveFilingsQueryDataSources(
  context: Pick<FinsterQueryToolContext, "dataSources" | "selectionMode">,
): FinsterQueryDataSource[] | null {
  if (context.selectionMode === "filtered") {
    const selected = new Set(context.dataSources ?? []);
    const intersected = FILINGS_LITE_DATA_SOURCES.filter((source) =>
      selected.has(source)
    );
    return intersected.length > 0 ? intersected : null;
  }
  return [...FILINGS_LITE_DATA_SOURCES];
}

/**
 * Execute focused filings/IR research through the established research
 * transport. Delegation intentionally preserves streaming sub-events,
 * cancellation, citation side channels, user-Skill guidance, workbook
 * attribution, and Langfuse metadata.
 *
 * Uploaded-file scopes and explicit filters with no eligible official source
 * fail closed and direct the model to the full research tool.
 */
export async function executeFilingsQueryToolDetailed(
  rawInput: unknown,
  signal?: AbortSignal,
  context?: FinsterQueryToolContext,
): Promise<{
  output: string;
  observationMetadata?: Record<string, unknown>;
  researchCitations?: unknown[];
}> {
  if (
    !context ||
    context.scopeAvailable === false ||
    !Array.isArray(context.dataSources) ||
    !Array.isArray(context.userFileIds) ||
    !["default", "filtered", "user_files"].includes(context.selectionMode ?? "")
  ) {
    return {
      output: JSON.stringify({
        error:
          "Research source scope is unavailable for this Excel run. " +
          "The filings request was not sent to avoid using unselected sources.",
      }),
    };
  }
  if (
    context.selectionMode === "user_files" ||
    (context.userFileIds?.length ?? 0) > 0
  ) {
    return {
      output: JSON.stringify({
        error:
          "filings_query does not run against uploaded files. Call the full " +
          "research tool instead so every selected file remains in scope.",
      }),
    };
  }

  const dataSources = resolveFilingsQueryDataSources(context);
  if (dataSources === null) {
    return {
      output: JSON.stringify({
        error:
          "None of the filings sources (SEC Filings, Investor Relations, " +
          "Global Filings) are enabled in the current source selection. " +
          "Call the full research tool instead; it will preserve the user's filter.",
      }),
    };
  }

  return executeFinsterQueryToolDetailed(rawInput, signal, {
    ...context,
    dataSources,
    userFileIds: [],
    selectionMode: "filtered",
  });
}

